import { ContentStateInterface, EntryMode } from '../../../@types/types'
import { DispatchType } from '../../../@types/redux-types'
import {
  insertionUpdateError,
  insertionUpdateSuccess,
  updateInsertionId,
} from '../../reducers/insertion/insertionFeature'
import { realEstateToApi } from '../../../api/realEstate/converters/toApi/realEstateToApi'
import {
  AccessForbiddenError,
  ClbRealEstateNotFoundError,
  RealEstateNotFoundError,
  saveAndGetRealEstateListing,
} from '../../../api/realEstate/realEstateApi'
import { clearMultimediaOperations, setAttachmentsOnOfferPublish } from '../../reducers/multimedia/multimediaFeature'
import { InitState } from '../../reducers/root/initialState'
import { getDefaultLogger } from '../../../services/logger/logger'
import { RealEstate, RealEstateDTO } from '../../../@types/real-estate-api-types'
import { attachmentsToApp } from '../../../api/realEstate/converters/toApp/attachmentsToApp'
import {
  accessForbidden,
  listingDeleted,
  progressError,
  sessionNotFound,
} from '../../reducers/errorHandler/errorHandlerFeature'
import { showMessage } from '../../reducers/message/messageFeature'
import { tracking } from '../../../tracking/tracking'
import { FlowState } from '../../../@types/state-types'
import { buildExitUrl } from '../../../utils/exitUrlBuilder/exitUrlBuilder'
import { getAiTextLogContext, logAiTextInvestigation } from '../suggestedText/suggestedTextLogging'

export const saveAndProgress =
  (insertionData: ContentStateInterface, isSubmitBtnClicked: boolean) =>
  async (dispatch: DispatchType, getState: () => InitState): Promise<void> => {
    await waitForBackendCalls(getState)

    // querying the store after waiting for the other calls to finish
    const { multimediaFeature, flowFeature, addressFeature, insertionFeature, userFeature } = getState()
    const isWaitingListFlow = flowFeature.flowInformation.options?.isWaitingListFlow

    const isFirstSave = !insertionFeature.insertionIdentification.realEstateId // realEstateId is set after the first save
    const hadExternalIdBefore =
      !!insertionFeature.insertionIdentification.unitId && !!insertionFeature.insertionIdentification.buildingId

    const realEstateObject: RealEstateDTO = realEstateToApi(
      insertionFeature.insertionIdentification,
      insertionData,
      multimediaFeature,
      flowFeature,
      addressFeature
    )
    getDefaultLogger().info(`Redirect is ${isSubmitBtnClicked}`)

    try {
      const response: RealEstate = await saveAndGetRealEstateListing(
        insertionFeature.insertionIdentification.id,
        realEstateObject,
        {
          requestId: getState().suggestedTextFeature.lastCompletedRequestId,
          listingSessionId: insertionFeature.insertionIdentification.id,
        }
      )
      handleSaveSuccess(dispatch, response, realEstateObject, isSubmitBtnClicked, {
        isFirstSave,
        hadExternalIdBefore,
        flowFeature,
        insertionFeature,
        isWaitingListFlow,
      })

      if (isSubmitBtnClicked) {
        await redirectAfterSubmit(userFeature, response, isWaitingListFlow)
      }
      dispatch(insertionUpdateSuccess())
      logAiTextInvestigation('save_succeeded', {
        isSubmitBtnClicked,
        responseListingId: response.id,
        responseRealEstateId: response.realEstateId,
        ...getAiTextLogContext(getState()),
      })
    } catch (error) {
      logAiTextInvestigation('save_failed', {
        isSubmitBtnClicked,
        ...getSanitizedSaveErrorLogContext(error),
        ...getAiTextLogContext(getState()),
      })
      handleSaveError(dispatch, error, realEstateObject, isSubmitBtnClicked)
    }
  }

function handleSaveSuccess(
  dispatch: DispatchType,
  response: RealEstate,
  realEstateObject: RealEstateDTO,
  isSubmitBtnClicked: boolean,
  {
    isFirstSave,
    hadExternalIdBefore,
    flowFeature,
    insertionFeature,
    isWaitingListFlow,
  }: {
    isFirstSave: boolean
    hadExternalIdBefore: boolean
    flowFeature: FlowState
    insertionFeature: any
    isWaitingListFlow: boolean | undefined
  }
) {
  dispatch(clearMultimediaOperations())
  dispatch(
    updateInsertionId({
      externalId: response.externalId!,
      realEstateId: response.realEstateId!,
      unitId: response.unitId,
      buildingId: response.buildingId,
      id: response.id,
      progressState: response.state!,
    })
  )
  dispatch(
    setAttachmentsOnOfferPublish({
      attachmentsToAdd: attachmentsToApp(response.attachments || []),
      attachmentsToDelete: realEstateObject.attachments,
    })
  )

  logEventsIfNeeded({
    isFirstSave,
    hadExternalIdBefore,
    response,
    flowFeature,
    insertionFeature,
    isSubmitBtnClicked,
    isWaitingListFlow,
  })
}

function logEventsIfNeeded({
  isFirstSave,
  hadExternalIdBefore,
  response,
  flowFeature,
  insertionFeature,
  isSubmitBtnClicked,
  isWaitingListFlow,
}: {
  isFirstSave: boolean
  hadExternalIdBefore: boolean
  response: RealEstate
  flowFeature: FlowState
  insertionFeature: any
  isSubmitBtnClicked: boolean
  isWaitingListFlow: boolean | undefined
}) {
  const entryMode = insertionFeature.insertionIdentification.metaInformation?.entryMode

  if (isFirstSave) {
    traceCreationEvent(
      'listing_create',
      flowFeature,
      entryMode,
      isSubmitBtnClicked,
      response.realEstateId,
      isWaitingListFlow
    )
  }

  const hasExternalIdAfter = Boolean(response.unitId || response.buildingId)
  if (!hadExternalIdBefore && hasExternalIdAfter) {
    traceCreationEvent('object_create', flowFeature, entryMode, isSubmitBtnClicked, response.realEstateId)
  }
}

function getSanitizedSaveErrorLogContext(error: unknown) {
  if (error instanceof AccessForbiddenError) {
    return {
      errorType: 'access_forbidden',
      errorStatus: 403,
    }
  }

  if (error instanceof RealEstateNotFoundError) {
    return {
      errorType: 'real_estate_not_found',
      errorStatus: 404,
    }
  }

  if (error instanceof ClbRealEstateNotFoundError) {
    return {
      errorType: 'clb_real_estate_not_found',
      errorStatus: 404,
    }
  }

  return {
    errorType: error instanceof Error ? error.name || 'error' : 'unknown',
  }
}

function traceCreationEvent(
  eventName: string,
  flowFeature: FlowState,
  entryMode: EntryMode | undefined,
  wait: boolean,
  realEstateObjectID?: number,
  isWaitingListFlow?: boolean
) {
  const param3 = flowFeature.flow?.flowInformation.metaData.segmentation
    ?.toLowerCase()
    .replace('property_owner', 'owner')

  tracking(
    {
      eventName,
      flowInformation: flowFeature.flowInformation,
      entryMode,
      moreEventParameters: {
        ...(isWaitingListFlow ? { event_parameter_2: 'waiting_list' } : {}),
        event_parameter_3: param3,
        event_parameter_4: flowFeature.flow?.flowInformation.metaData.category.toLowerCase(),
        ...(realEstateObjectID ? { ga_cd_realestate_object_id: realEstateObjectID.toString() } : {}),
      },
    },
    wait
  )
}

async function redirectAfterSubmit(userFeature: any, response: RealEstate, isWaitingList: boolean = false) {
  // Wait for comms service to receive the listing data before redirecting to waiting list
  if (isWaitingList) {
    await new Promise((resolve) => setTimeout(resolve, 5000))
  }

  window.location.href = buildExitUrl(
    userFeature.contingentCustomer,
    response.realEstateId!,
    response.metaInformation?.entryMode!,
    response.segmentation,
    response.transaction,
    response.category,
    response.buildingId,
    response.unitId,
    isWaitingList,
    response.published,
    response.state
  )
}

function handleSaveError(
  dispatch: DispatchType,
  error: unknown,
  realEstateObject: RealEstateDTO,
  isSubmitBtnClicked: boolean
) {
  dispatch(insertionUpdateError())
  const LOG_PREFIX = 'Save-Failed: '

  if (error instanceof AccessForbiddenError) {
    logAndDispatchError(dispatch, LOG_PREFIX, realEstateObject, 'access forbidden', accessForbidden)
  } else if (error instanceof RealEstateNotFoundError) {
    logAndDispatchError(dispatch, LOG_PREFIX, realEstateObject, 'not found(RealEstateNotFoundError)', listingDeleted)
  } else if (error instanceof ClbRealEstateNotFoundError) {
    logAndDispatchError(
      dispatch,
      LOG_PREFIX,
      realEstateObject,
      'not found (ClbRealEstateNotFoundError)',
      sessionNotFound
    )
  } else if (isSubmitBtnClicked) {
    redirectToErrorPage(`${LOG_PREFIX}${realEstateObject.estate.id} errored, redirect to error page`, dispatch)
  } else {
    handleUnknownError(dispatch, LOG_PREFIX, realEstateObject, error)
  }
}

function logAndDispatchError(
  dispatch: DispatchType,
  logPrefix: string,
  realEstateObject: RealEstateDTO,
  message: string,
  action: any
) {
  getDefaultLogger().info(`${logPrefix}${realEstateObject.estate.id} ${message}`)
  dispatch(action())
}

function handleUnknownError(
  dispatch: DispatchType,
  logPrefix: string,
  realEstateObject: RealEstateDTO,
  error: unknown
) {
  dispatch(showMessage({ message: 'unknown' }))
  getDefaultLogger().warn(
    `${logPrefix}Received error status from offer api -> show warning. RealEstateObject id: ${realEstateObject.estate.id} number of attachments: ${realEstateObject.attachments?.length}`,
    error
  )
}

function waitForBackendCalls(getState: () => InitState) {
  const timeoutMs = 5000
  const intervalMs = 50

  return new Promise<void>((resolve, reject) => {
    const start = Date.now()

    const check = () => {
      const { isLoading, isPending } = getState().addressFeature

      if (!isLoading && !isPending) {
        resolve()
        return
      }

      if (Date.now() - start >= timeoutMs) {
        reject(new Error('Timeout while waiting for calls to finish before executing saveAndProgress'))
        return
      }
      setTimeout(check, intervalMs)
    }
    check()
  })
}

export function redirectToErrorPage(errorMessage: string, dispatch: DispatchType, error?: unknown) {
  getDefaultLogger().warn(errorMessage, error)
  dispatch(progressError({ replaceHistory: true }))
}
